import { DateTime } from 'luxon';
import axios from 'axios';
import _cloneDeep from 'lodash/cloneDeep';
import _difference from 'lodash/difference';
import _get from 'lodash/get';
import _isArray from 'lodash/isArray';
import _unset from 'lodash/unset';
import _set from 'lodash/set';
import _has from 'lodash/has';
import _findIndex from 'lodash/findIndex';
import _eachDeep from 'deepdash/eachDeep';
import _isEmpty from 'lodash/isEmpty';
import { constants, timeConstants, EMPTY_PRODUCTID } from '@/lib/constants';
import store from '@/store';
import { APIException } from '@/lib/exceptions';

const hasErrors = (obj) => {
  let retval = false;
  if (obj && typeof obj === 'object') {
    for (const prop of Object.keys(obj)) {
      /* istanbul ignore next */
      if (prop === undefined || prop === null) continue;
      if (typeof prop === 'string' && prop.charAt(0) === '_' && prop.indexOf('_errors') > 0) retval = true;
      if (obj[prop] && retval !== true) retval = hasErrors(obj[prop]);
    }
  } // else it's a primative and we don't need to recurse anymore
  return retval;
};

const getErrors = (obj) => {
  let errors = [];
  for (const key of Object.keys(obj)) {
    const isError = key.slice(-7) === '_errors';
    if (isError) {
      for (let index = 0; index < obj[key].length; index += 1) {
        const error = obj[key][index];
        errors.push(error);
      }
      continue;
    }
    if (_isArray(obj[key])) {
      for (let index = 0; index < obj[key].length; index += 1) {
        errors = errors.concat(getErrors(obj[key][index]));
      }
    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
      errors = errors.concat(getErrors(obj[key]));
    }
  }
  return errors;
};

const _def = x => typeof x !== 'undefined';

/**
 * A short ES6 recursive reduce function
 * @param {Array} Array to reduce
 * @param {Function} Function to reduce against
 * @param {*} Accumulator
 * @param {number} Iteration to begin at
 */
const reduce = ([x, ...xs], f, acc, i = 0) => (_def(x) ?
  reduce(xs, f, f(acc, x, i), i + 1) : acc);

/**
 * HOF to change function to nonblocking
 * @param {Function} cb
 */
const requestIdleCallback = window.requestIdleCallback || (cb => {
  const start = Date.now();
  return setTimeout(() => {
    const data = {
      didTimeout: false,
      timeRemaining() {
        /* istanbul ignore next */
        return Math.max(0, 50 - (Date.now() - start));
      },
    };
    cb(data);
  }, 1);
});

/**
 * Merges two arrays of objects based on supplied key
 * @param {Object[]} firstArray
 * @param {Object[]} newArray
 * @param {String} key
 * @return {Array} mergedArrays
 */
const mergeArraysByKey = (firstArray, newArray, key) => {
  // Create one array from the two supplied arrays
  const mergedArrays = [...firstArray.concat(newArray)
    .reduce((acc, record) => {
      // Set and assign values from newArray to firstArray based on key
      return record && record[key] ?
        acc.set(record[key], Object.assign(acc.get(record[key]) || {}, record)) : acc;
    // Create a map and keep the array's original structure using only the maps values
    }, new Map()).values()];

  return mergedArrays;
};

/**
 * Rounds number based on provided precision.
 * @param {number} number The number to round
 * @param {number} [precision = 2] The precision to round to.
 * @return {number} Rounded number
 */
const roundTo = (number, precision = 2) => {
  const factor = 10 ** precision;
  return Math.round(number * factor) / factor;
};

/**
 * Rounds number based on provided precision.
 * Casts to string and ensures that its output is fixed to precision.
 * @param {number} number The number to round
 * @param {number} [precision = 2] The precision to round to.
 * @return {string} Rounded number string
 */
const precisionRound = (number, precision = 2) => {
  const roundedValue = roundTo(number, precision);
  if (Number.isNaN(roundedValue)) return '';
  return roundedValue.toFixed(precision);
};

const ruid = () => {
  const UIDCHARS = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'.split('');
  const chars = UIDCHARS;
  const uid = new Array(36);
  let rnd = 0;
  let r;
  for (let i = 0; i < 36; i += 1) {
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      uid[i] = '-';
    } else if (i === 14) {
      uid[i] = '4';
    } else {
      if (rnd <= 0x02) {
        rnd = 0x2000000 + (Math.random() * 0x1000000) | 0; // eslint-disable-line
      }
      r = rnd & 0xf; // eslint-disable-line
      rnd >>= 4; // eslint-disable-line
      uid[i] = chars[(i === 19) ? (r & 0x3) | 0x8 : r]; // eslint-disable-line
    }
  }
  return uid.join('');
};

const isPresent = (val) => {
  // Has the user provided a value? See server's analagous empty() function
  return !!(val || val === 0 || val === false);
};

/**
 * gets operator type based on operator
 * operator types are required for AdvancedSearch combobox autocomplete
 * @param {string} op - uql operator
 * @return {string} operator type
 */
const operatorType = (op) => {
  switch (op) {
    case 'NN':
    case 'NL':
      return 'nullOp';
    case 'Y':
    case 'N':
      // DEAD CODE (Y/N are NOT operators)
      return 'flagOp';
    case 'EQ':
    case 'NE':
      return 'exactOp';
    case 'GT':
    case 'GE':
    case 'LT':
    case 'LE':
      return 'equalityOp';
    case 'LD':
    case 'ED':
    case 'GD':
      return 'dayOp';
    case 'AN':
    case 'NA':
      return 'relatedColl';
    case 'HS':
    case 'NH':
      return 'relatedObj';
    default:
      return 'modifier';
  }
};

const isDayOp = (queryOverrides, fieldname) => {
  const overrideOp = queryOverrides.find(({ name }) => name === fieldname);
  return operatorType(overrideOp?.queryType) === 'dayOp';
};

const ORDER_TYPECOLOR = {
  SAL: '#2a2e58', // $ra-deep-blue
  RES: '#2a2e58', // $ra-deep-blue
  MCR: '#388E3C', // credit green
  MDB: '#0288D1', // debit blue
  EEX: '#C2185B', // exchange purple
  CRM: '#5D4037', // return brown
  SER: '#F57C00', // service orange
  SAL_DARK: '#383E7A',
  RES_DARK: '#383E7A',
  MCR_DARK: '#3F7A4B',
  MDB_DARK: '#3A6F9A',
  EEX_DARK: '#9A3F6A',
  CRM_DARK: '#4E423D',
  SER_DARK: '#B06F2F',
};

const ORDER_TYPECOLOR_LIGHT = {
  CRM: '#8D6E63', // return light brown
};

/**
 * Returns color based on order type
 * @param {string} orderType The order type (SAL, RES, MCR, MDB, EEX, CRM, or SER)
 * @param {boolean} [useLightColor = false] Should use the light color variant if available
 * @return {string} Order type color
 */
const getOrderTypeColor = (orderType, useLightColor = false) => {
  const theme = getCookie('theme');
  const isDark = (theme === 'system' && getSystemTheme() || theme) === 'dark';
  if (isDark) orderType += '_DARK';

  return useLightColor ?
    ORDER_TYPECOLOR_LIGHT[orderType] || ORDER_TYPECOLOR[orderType] :
    ORDER_TYPECOLOR[orderType];
};

const getButtonVariant = (dark) => dark ? 'outlined' : 'elevated';

/**
 * Dates from date picker get formatted the way the server expects it
 * @param {string} date - date from date pickers
 * @return {string} formatted date
 */
const formatUqlDateText = (date) => {
  const { UQLDATEFORMAT } = timeConstants;
  return DateTime.fromISO(date, { zone: 'utc', locale: 'en-US' }).toFormat(UQLDATEFORMAT);
};

/**
 * Convenient function for helping to format uql, mostly for use in parent component when emitted from executeSearch
 * @param {string} key the lefthand side of a uql statement
 * @param {*} val the value to query against
 * @param {string} operator an override value if the query is not using CONTAINSIGNORECASE, meant to pass constant from lib/constants
 */
const formatUql = (key, val, operator) => {
  // REFACTOR: this needs to also support "ABC*" and then
  // it should be the only DRY place, invoked from generateUql() so
  // that we can also add things like "!=" and "="
  const { CONTAINSIGNORECASE, DELIM, ISNOTNULL } = constants;
  const isWildCard = val === '*';
  return `${key}-${isWildCard ? ISNOTNULL : operator || CONTAINSIGNORECASE}${isWildCard ? '' : val}${DELIM}`;
};

/**
 * Formats date for displaying to the user
 * @param {Object} config - payload containing the date and date format
 * @param {String} date - the date string to be formatted
 * @param {String} [format] - output date format
 * @return {String} - formatted date
 */
const formatDateText = ({ date, format = 'iso', isMonth = false, withTime = false }) => {
  let newDate;
  if (format === 'iso') {
    newDate = DateTime.fromISO(date, { zone: 'utc', locale: 'en-US' });
  }
  if (format === 'sql' || (newDate && newDate.invalid)) {
    newDate = DateTime.fromSQL(date, { zone: 'utc', locale: 'en-US' });
  }
  if (isMonth) {
    return newDate.toLocaleString({ month: '2-digit', year: 'numeric' });
  }
  if (withTime) {
    // Both D and tt use locale settings but we don't want the unneeded comma:
    return newDate.toFormat('D tt');
  }
  return newDate.toLocaleString();
};

const formatAtpDate = (minAtpDate, indicators) => {
  const CANT_PROMISE_TEXT = store.getters['translations/label']('{CantPromise}l');
  const TODAY_TEXT = store.getters['translations/label']('{today}l');
  const FORMAT = 'yyyy-MM-dd';
  let text = '';
  const now = DateTime.local();
  const formattedAtpDate = minAtpDate.toFormat(FORMAT);
  const indicator = typeof indicators === 'object' ? indicators[formattedAtpDate] : indicators;
  if (minAtpDate.year === 9999) text = CANT_PROMISE_TEXT;
  else if (minAtpDate.startOf('day').ts === now.startOf('day').ts) text = `${TODAY_TEXT}${indicator ? ` (${indicator})` : ''}`;
  else text = `${minAtpDate.toFormat('MM/dd/yyyy')}${indicator ? ` (${indicator})` : ''}`;
  return text;
};

const formatAtpFromLine = (line) => {
  const { atpdate, atpindicator } = line;
  if (!atpdate) return '';
  const atpdateObj = DateTime.fromFormat(atpdate, 'yyyy-MM-dd');
  return formatAtpDate(atpdateObj, atpindicator);
};

const getDatesAndQtys = (detail, indicators, datesAndQtys) => {
  const { atpdate, atpindicator, qtyordered } = detail;
  const atpdateObj = DateTime.fromFormat(atpdate, 'yyyy-MM-dd');
  const formattedDate = formatAtpDate(atpdateObj, indicators);
  if (datesAndQtys[formattedDate]) datesAndQtys[formattedDate].qtyordered += qtyordered;
  else datesAndQtys[formattedDate] = { qtyordered, atpindicator, atpdate };
};

const orderLineAtpDisplay = (orderline) => {
  if (!orderline.orderdetails || !orderline.atpdate) return '';
  const indicators = {};
  const FORMAT = 'yyyy-MM-dd';
  const datesAndQtys = {};
  let minAtpDate = DateTime.fromFormat(orderline.atpdate, FORMAT);
  for (const detail of orderline.orderdetails) {
    if (!detail.atpdate) continue;
    const atpDate = DateTime.fromFormat(detail.atpdate, FORMAT);
    const diff = atpDate.diff(minAtpDate, 'hours').toObject();
    if (diff.hours < 0) minAtpDate = atpDate;
    if (detail.atpindicator) indicators[detail.atpdate] = detail.atpindicator;
    getDatesAndQtys(detail, indicators, datesAndQtys);
  }
  const partial = Object.keys(datesAndQtys).length > 1;
  const formattedDate = formatAtpDate(minAtpDate, indicators);
  return `${partial ? 'Partially ' : ''}${formattedDate} ${partial ? `(Qty ${datesAndQtys[formattedDate].qtyordered})` : ''}`;
};

const formatAtpBreakdown = orderline => {
  const datesAndQtys = {};
  const indicators = {};
  for (const detail of orderline.orderdetails) {
    if (!detail.atpdate) continue;
    if (detail.atpindicator) indicators[detail.atpdate] = detail.atpindicator;
    getDatesAndQtys(detail, indicators, datesAndQtys);
  }

  /**
   * Sort by atpdate in ascending order. atpdate =/= atpDisplay (e.g. Today/Partially/**Unknown**)
   * Map sorted dates to array of messages to be displayed in breakdown popover
   */
  const messages = Object.entries(datesAndQtys);
  if (messages.length < 2) return [];

  return messages.sort(([, valueA], [, valueB]) => {
    if (valueA.atpdate > valueB.atpdate) return 1;
    if (valueA.atpdate < valueB.atpdate) return -1;
    return 0;
  }).map(([key, value]) => {
    return `${key} (Qty ${value.qtyordered})`;
  });
};

const qtyFilledLabel = (item, isServiceType = false) => {
  if (item.qtyordered === 0 || item.product.stockeditem !== 'Y' || isServiceType) return '(Fill N/A)';
  const filled = item.qtyfilled || 0;
  return (item._classname === 'ServiceDetail' && item.fillsiteid || item.qtyordered === filled) ?
    '(Filled)' :
    `Filled: ${filled}`;
};

const formatFillDetails = line => {
  /**
   * Ported from Flex
   */
  if (line.qtyordered === 0 || line.product.stockeditem !== 'Y' || !line.orderdetails) {
    return { fillDetailsDisplay: '', fillDetailsBreakdown: [] };
  }
  const sites = {};
  let locations = {};
  for (const { filllocationid, fillsiteid, voided, qtyordered } of line.orderdetails) {
    if (filllocationid && fillsiteid && voided !== 'Y') {
      if (!_has(sites, fillsiteid)) {
        sites[fillsiteid] = {};
      }
      locations = sites[fillsiteid];
      if (locations[filllocationid]) {
        locations[filllocationid] += qtyordered;
      }
      else {
        locations[filllocationid] = qtyordered;
      }
    }
  }
  let resultLines = [];
  let currentSite = null;
  let currentLoc = null;
  for (const site of Object.keys(sites)) {
    for (const location of Object.keys(sites[site])) {
      currentSite = site;
      currentLoc = location;
      resultLines.push(`${site}: ${location} (Qty ${sites[site][location]})`);
    }
  }
  let fillDetailsDisplay = '';
  let fillDetailsBreakdown = '';
  if (resultLines.length) {
    if (resultLines.length === 1 && _get(sites, `${currentSite}.${currentLoc}`) === line.qtyordered) {
      fillDetailsDisplay = `${currentSite}: ${currentLoc}`;
    }
    else {
      resultLines.sort();
      fillDetailsBreakdown = resultLines;
      if (resultLines.length > 1) {
        resultLines = resultLines.slice(0, 1);
        fillDetailsDisplay = '...';
      }
      fillDetailsDisplay = resultLines.join('\n') + fillDetailsDisplay;
    }
    fillDetailsDisplay = `Filled At: ${fillDetailsDisplay}`;
    return { fillDetailsDisplay, fillDetailsBreakdown };
  }
  return { fillDetailsDisplay, fillDetailsBreakdown: [] };
};

/**
 * Formats the given input into a safe string which
 * has been lowercased. Also handles null edge case
 * @param {*} val - value to be formatted
 * @return {string} formatted value
 */
const formatValue = (val = '') => {
  if (val === null) val = '';
  return val.toString().toLowerCase();
};

const pruneForServerCall = (obj) => {
  if (!obj) return;

  const keys = Object.keys(obj);
  // Only prune transfer objects (objects with _classname):
  if (!keys.includes('_classname')) return;

  const skippedKeys = ['_classname', 'original', '_ui_label'];
  for (const key of keys) {
    // Skip pruning _classname key and originals (but note, originals should
    // never be touched anyway since they won't/shouldn't have _classname):
    if (skippedKeys.includes(key) || key.startsWith('original_')) continue;
    if (key.startsWith('_')) {
      _unset(obj, key);
    } else {
      const value = obj[key];
      if (typeof value === 'object') {
        if (value instanceof Array) {
          for (const item of value) pruneForServerCall(item);
        } else pruneForServerCall(value);
      }
    }
  }
};

/**
 * Wrapper around axios.post to format passed in params
 * and make endpoint request. Returns the results of the
 * axios post request.
 * @param {string} url - url endpoint
 * @param {Object} [rawParams] - parameters to be passed into axios
 * @return {*} results from post request
 */
const post = async (url, rawParams = {}, returnResponse = false, additionalConfig = {}) => {
  const clonedParams = _cloneDeep(rawParams);
  const params = new URLSearchParams();

  for (const key of Object.keys(clonedParams)) {
    const type = typeof clonedParams[key];
    const canPrune = type === 'object' && !_isArray(clonedParams[key]);
    if (!canPrune) continue;
    pruneForServerCall(clonedParams[key]);
  }
  for (const [key, param] of Object.entries(clonedParams)) {
    /* TODO:#12846
     * Upgrade to axios v0.22.0
     * CancelToken is deprecated
     * Should use AbortController - https://axios-http.com/docs/cancellation
     * Also, we have additionalConfig specifically for use of data passed to axios.post
     * no need to filter through request params to pluck out 'cancelToken'
     */
    params.append(key, typeof param === 'object' ? JSON.stringify(param) : param);
  }
  let search;
  try {
    search = await axios.post(url, params, additionalConfig);
    const payload = {
      results: _get(search, 'data.response.results', []),
      refresh_others: _get(search, 'data.response.refresh_others', []),
      errors: _get(search, 'data.response.errors', false),
      warnings: _get(search, 'data.response.warnings', false),
      notification: _get(search, 'data.response.notification', null),
      notification_msg: _get(search, 'data.response.notification_msg', null),
      metaData: search,
    };

    // Return entire response when a key of interest isn't results, errors, or warnings.
    if (returnResponse) payload.response = _get(search, 'data.response', {});

    return payload;
  } catch (callError) {
    throw new APIException(callError);
  }
};

/**
 * Appends `text` and `value` properties to data to
 * for use for dropdown selects. When passed in two arguments,
 * the textPath represents the path on the object for the label and
 * valuePath represents the path on the object for the value.
 * If only passed textPath, then the same path is used for both label and value
 * @param {Object[]} data - data set
 * @param {string} textPath - path on data object for select label
 * @param {string} [valuePath] - path on data object for select value
 * @return {Object[]} transformed array of objects
 */
const transformToInputItem = (data, textPath, valuePath) => {
  if (!Array.isArray(data) && typeof data === 'object') data = [data];

  // if only 1 path passed in, use for both text and value
  if (textPath && !valuePath) valuePath = textPath;

  return data.map(item => ({
    ...item,
    text: _get(item, textPath, null),
    value: _get(item, valuePath, null),
  }));
};

/**
 * Truncates text based on a provided maximum character limit. On all truncated text
 * the remaining characters will be replaced with ellipses
 * @param {string} text - Text to truncate
 * @param {number} maxChars - Maximum amount of characters before truncation begins
 * @return {string} text truncated with ellipses
 */
const truncate = (text, maxChars = 35) => {
  if (typeof text !== 'string' || text.length <= maxChars) return text;
  const truncatedStr = text.substring(0, maxChars);
  return `${truncatedStr}...`;
};

/**
 * Attempts to access data from an object based on a string path.
 * If nothing is found, the altNestedPaths array is iterated over to
 * provide additional keys to traverse in the object for the value
 * @param {Object} object - The object to query
 * @param {string} path - The path of the property to get.
 * @param {string[]} [altNestedPaths] - Additional nested paths to traverse
 * @param {Boolean} [strict = false] - Check altNestedPaths is value is null as well
 * @return {*} value found at the path specified, if not found returns empty string
 */
const getValue = (object, path, altNestedPaths = [], strict = false) => {
  const val = _get(object, path);
  if (val !== undefined && (strict ? val !== null : true)) return val;

  for (const altNestedPath of altNestedPaths) {
    const value = _get(object, `${altNestedPath}.${path}`);
    if (value) return value;
  }

  return '';
};

/**
 * Sanitizes image URL for output. Checks for presence of ://
 * to conditionally append origin if missing.
 * @param {string} url - input URL
 * @return {string} Formatted image URL
 */
const formatImageURL = (url) => {
  if (typeof url !== 'string' || url === '') return '';
  return url.includes('://') ? url : `${window.location.origin}/${url}`;
};

const filterHeadersByPermission = (mappedHeaders) => {
  return mappedHeaders.filter(({ permission = null }) => {
    if (permission) return store.getters['permissions/isPermPresent'](permission);
    return true;
  });
};

/**
 * Conforms to a whitelist of headers and iterates over db props
 * to return an array of objects transformed for usage in RDataTable
 * This method must be called on all customerHeaders.
 * @param {Object[]} dbProps - Array of db_props
 * @param {string[]} allowedHeaders - whitelist of headers
 * @return {Object[]} Array of RDataTable Header formatted objects
 */
const formatHeaders = (dbProps, allowedHeaders = []) => {
  let formattedDbProps = dbProps.reduce((acc, prop) => {
    const alreadyProcessed = acc.some(({ value }) => value === prop.name);
    if (alreadyProcessed) return acc;
    if (allowedHeaders.length && !allowedHeaders.includes(prop.name)) return acc;

    const className = _get(prop, 'data.references.foreign_class') || prop.className;
    const foreignKeyLink = _get(prop, 'data.references.fk_link');
    const type = prop.data.type;
    const dataType = _get(prop, 'data.property_meta.datatype', '');

    let align;
    if (type === 'number' || dataType === 'currency') align = 'end';

    let title = '';
    if (typeof _get(prop, 'data.labels.label') === 'string') title = prop.data.labels.label;
    else if (typeof _get(prop, 'data.labels') === 'string') title = prop.data.labels;

    acc.push({
      title,
      value: prop.name,
      dataType: prop.data.type,
      format: prop.data.format,
      permission: _get(prop, 'data.property_meta.permission'),
      align,
      className,
      foreignKeyLink,
      sortable: true,
    });
    return acc;
  }, []);

  formattedDbProps = filterHeadersByPermission(formattedDbProps);
  if (allowedHeaders.length) {
    const byIndex = (a, b) => allowedHeaders.indexOf(a.value) - allowedHeaders.indexOf(b.value);
    formattedDbProps.sort(byIndex);
  }
  return formattedDbProps;
};

/**
 * Fetches ordered headers based on classname and user preference
 * @param {String} className - classname to fetch headers for
 * @param {Object} meta - meta data for the class
 * @param {Object[]} passedDbProps - override db properties
 * @param {Object[]} customHeaders - custom pre-formatted headers
 * @param {Boolean} setSelected - choose to set 'selected' on headers rather than
 *                                remove them from the allowed headers
 * @return {Object[]} - Array of ordered headers
 */
const getOrderedHeaders = ({ className, meta, passedDbProps, passedPrefs, prefName, customHeaders = [], setSelected = false }) => {
  let dbProps = [];
  let orderPref;
  let hidePref;
  let columnOrderPreference;
  let columnHidePreference;

  // No need for dbProps if we already have headers
  if (!customHeaders.length) {
    dbProps = meta && !passedDbProps ?
      Object.entries(meta.properties).map(([name, data]) => ({ name, data })) :
      passedDbProps || store.getters['meta/db_properties'](className);
  }

  if (passedPrefs) {
    columnOrderPreference = passedPrefs.order;
    columnHidePreference = passedPrefs.hide;
  } else if (prefName) {
    orderPref = `GridColOrder.${prefName}`;
    hidePref = `GridColHide.${prefName}`;
  } else {
    orderPref = `GridColOrder.DynaForm.${className}`;
    hidePref = `GridColHide.DynaForm.${className}`;
  }

  if (!columnOrderPreference) {
    columnOrderPreference = _get(store.getters['preferences/getPreference'](orderPref), 'preference');
  }
  if (!columnHidePreference) {
    columnHidePreference = _get(store.getters['preferences/getPreference'](hidePref), 'preference');
  }

  // If we have customHeaders, we already know what headers are allowed
  let allowedHeaders = customHeaders.length ?
    customHeaders.map(({ value }) => value) :
    dbProps.map(({ name }) => name);

  // Determine what columns we want to hide
  const columnHide = columnHidePreference && columnHidePreference.value ?
    columnHidePreference.value.split(',') :
    null;
  if (!setSelected && columnHide) {
    allowedHeaders = _difference(allowedHeaders, columnHide);
  }

  // Sort the headers based on user preference, append remaining to the end
  if (columnOrderPreference && columnOrderPreference.value) {
    const columnOrder = columnOrderPreference.value.split(',');
    const orderedHeaders = [];
    for (const col of columnOrder) {
      const index = allowedHeaders.findIndex(hdr => hdr === col);
      if (index === -1) continue;
      orderedHeaders.push(col);
      allowedHeaders.splice(index, 1);
    }
    allowedHeaders = [...orderedHeaders, ...allowedHeaders];
  }

  // If we have customHeaders, reorder them, otherwise call formatHeaders
  const formattedHeaders = customHeaders.length ?
    allowedHeaders.map(hdr => customHeaders.find(({ value }) => value === hdr)) :
    formatHeaders(dbProps, allowedHeaders);
  return setSelected ?
    formattedHeaders.map(item => {
      const selected = columnHide === null || !columnHide.includes(item.value);
      return { ...item, prefSelected: selected, selected };
    }) :
    formattedHeaders;
};

/**
 * Generates custom meta for models that are not in the server meta data
 * @param {Object[]} dbProps - Array of db_props
 * @param {string[]} allowedHeaders - whitelist of headers
 * @return {Object} Object of with allowedHeaders as keys
 */
const generateMeta = (dbProps, allowedHeaders) => {
  const formattedProps = dbProps.reduce((acc, prop) => {
    const alreadyProcessed = acc[prop.name] || null;
    const foundInAllowedHeaders = allowedHeaders.includes(prop.name);

    if (alreadyProcessed || !foundInAllowedHeaders) return acc;

    acc[prop.name] = {
      text: prop.data.labels.label,
      value: prop.name,
      dataType: prop.data.type,
      format: prop.data.format,
      property_meta: prop.data.property_meta || {},
    };

    return acc;
  }, {});

  return formattedProps;
};

/**
 * Gets primary keys from schema
 * @param {Object} schema
 * @return {Array} filtered primary keys
 */
const filterPrimaryKeys = (schema) => {
  return schema.primary_keys.concat();
};

/**
 * Utiltiy method responsible for translating errors and warnings.
 * @param {String} key = '' - Corresponds to a key within model prop.
 * This key must be formatted as either _name_errors or _name_warnings
 * in order for translations to lookup translated messages.
 * @param {Object} model - Object containing all data pertinent to record.
 * @param {Array[]} messages - Flattened collection of error/warning messages
 * represented as an coalesced array of array error or warning messages.
 * @param {Object} store - Object representing the store, used to lookup translations.
 * @return {Array} - Returns an array of translated messages to be
 * displayed underneath RInput field.
 */
const translateMessages = (messages, localStore) => {
  if (!messages) return [];

  const flattenMessages = (m) => {
    if (!Array.isArray(m) || !m.length || !Array.isArray(m[0])) return m;
    let flattened = [];
    m.forEach(msg => {
      if (Array.isArray(msg) && Array.isArray(msg[0])) {
        flattened = flattened.concat(flattenMessages(msg));
      } else {
        flattened.push(msg);
      }
    });
    return flattened;
  };

  // return unique messages
  const flattenedMessages = flattenMessages(messages);
  const translated = flattenedMessages.reduce((acc, msg) => {
    const res = localStore.getters['translations/error'](msg);
    if (isPresent(res) && !Array.isArray(res)) acc.push(res);
    return acc;
  }, []);
  return [...new Set(translated)];
};

/**
 * Utility method that filters and sorts required db fields
 * @param {*} fieldNames - list of required fields
 * @param {*} dbFields -db properties of class
 * @return {Object[]} - array of filtered and sorted
 */
const getRequiredFields = (fieldNames, dbFields) => {
  const requiredFields = dbFields.filter(field => fieldNames.includes(field.name));
  return requiredFields.sort((a, b) => fieldNames.indexOf(a.name) - fieldNames.indexOf(b.name));
};

/**
 * Calculates total account entry amount
 * @param {Object[]} records - array containing rows containing individual debit amounts
 * @param {String} accountEntryType - account entry type - either D (debit) or C (credit)
 * @return {Object} object containing rounded number and number formatted as a string
 */
const calculateTotal = (records, accountEntryType) => {
  if (!records || records.length === 0) return { formatted: '$0.00' };

  let total = 0.0;
  let amountType;
  const existingRecords = records.filter(record => !record._isDeleted);
  for (const { type, amount = 0, postingamount = 0 } of existingRecords) {
    amountType = amount || postingamount;
    if (type === accountEntryType && amountType) total += parseFloat(amountType);
  }

  const roundedTotal = precisionRound(total);
  return { roundedTotal, formatted: `$${roundedTotal}` };
};

/**
 * Calculates balance between credit and debit
 * @param {Object[]} records - array containing rows to create balance from
 * @return {Object} object containing rounded number and number formatted as a string
 */
const calculateBalance = (records, credits, debits) => {
  if (!records || records.length === 0) return { formatted: '$0.00' };

  const credit = credits.roundedTotal;
  const debit = debits.roundedTotal;
  const balance = parseFloat(debit) - parseFloat(credit);
  const roundedTotal = precisionRound(balance);
  const negative = roundedTotal < 0.00;

  let formatNegative = '';
  if (negative) {
    formatNegative = `${roundedTotal.slice(0, 1)}$${roundedTotal.slice(1)}`;
  }

  return { balance, formatted: negative ? formatNegative : `$${roundedTotal}` };
};

const generateLinkFromKey = (primaryKeys, row, className) => {
  const primaryKeysWithValues = {};
  for (const key of primaryKeys) {
    if (!isPresent(row[key])) return false;
    primaryKeysWithValues[key] = row[key];
  }
  const lastKey = primaryKeys[primaryKeys.length - 1];
  const slug = row[lastKey];
  _unset(primaryKeysWithValues, lastKey);
  return {
    path: `/${className}/${encodeURIComponent(slug)}`,
    query: primaryKeysWithValues,
    pk: lastKey,
  };
};

/**
 * Evaluate two dates against eachother using their ms since epoch
 * @param {string} firstDate the first date to compare against
 * @param {string} secondDate the second date to compare against
 * @param {function} comparator an optional function to control what the comparison is
 * @returns {number|null} 1 if firstDate is larger than second, -1 if firstDate is smaller than second, 0 if equal, else null
 */
const compareDates = (firstDate, secondDate, comparator) => {
  if (comparator === undefined) {
    comparator = (date1, date2) => {
      if (date1 > date2) return 1;
      if (date1 < date2) return -1;
      if (date1 === date2) return 0;
      return null;
    };
  }
  const firstParsedDate = Date.parse(firstDate);
  const secondParsedDate = Date.parse(secondDate);
  return comparator(firstParsedDate, secondParsedDate);
};

/**
 * Encodes an image as base64 data
 * @param {file} data the direct file upload
 * @return {string} base64 encoded data
 */
const encodeImageData = (data) => {
  let binary = '';
  const bytes = new Uint8Array(data);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

/**
 * Moves element within an array from and to a specified index position.
 * @param {Array} array - Array to modify.
 * @param {number} fromIndex - Index position of the element that needs moved.
 * @param {number} toIndex - Index position where element should be moved to.
 */
const moveElement = (array, fromIndex, toIndex) => {
  if (toIndex === fromIndex) return array;

  const target = array[fromIndex];
  const increment = toIndex < fromIndex ? -1 : 1;

  for (let index = fromIndex; index !== toIndex; index += increment) {
    array[index] = array[index + increment];
  }
  array[toIndex] = target;

  return array;
};

/**
 * Object containing any keys that are missing that that need to be on the model.
 */
const additionalModelKeys = {
  ArTran: {
    financecompanyid: null,
  },
  CreditApplication: {
    accounttype: 'I',
    comments: [],
    jointspouse: 'N',
  },
  Customer: {
    comments: [],
    warnings: [],
  },
  CustomerOrder: {
    closed: false,
    currentorderpath: null,
    customer: null,
    deletediscounts: null,
    discountsummaries: null,
    mainorderid: null,
    newmainorderid: null,
    restricted_attributes: null,
    totalbalancedue: 0,
    totalcredittotal: 0,
    totaldeliverycharge: 0,
    totalfinancebalancedue: null,
    totalfinancedamount: null,
    totalfinancepaymentstotal: 0,
    totallinetax: 0,
    totalorderbalance: 0,
    totalordertotal: 0,
    totalpaymentstotal: 0,
    totalregularsubtotal: 0,
    totalresolvedbalance: 0,
    totalsalesubtotal: 0,
    totalservicedue: null,
    totalsetupcharge: 0,
    totalsubtotal: 0,
    totaltax: 0,
    totaltentativefinancedamount: null,
    comments: [],
    tasks: [],
    orders: [],
  },
  DataGenerator: {
    parameters: [],
    reports: [],
    generator: null,
  },
  InstallmentContract: {
    financecode: 'DEF',
    currentdeposittotal: 0,
    jointlife: 'N',
    singlelife: 'N',
    property: 'N',
    accidenthealth: 'N',
    unemployment: 'N',
    totalsale: 0,
    equalpayments: 'N',
    manualfinancecredit: null,
    comments: [],
  },
  Invoice: {
    merchamount: 0,
    otheramount: 0,
    comments: [],
    discounts: [],
    duedates: [],
    glaccounts: [],
    payments: [],
    problems: [],
    purchaseorderlinks: [],
    receipts: [],
    costs: [],
    amountdiscounted: null,
    ignorematchproblems: null,
  },
  InvoiceDiscount: {
    amount: 0,
  },
  Order: {
    deliverytype: 'D',
    salesperson1alias: null,
    salesperson2alias: null,
    status: 'O',
    restricted_attributes: {},
    orderlines: [],
    adjustments: [],
    payments: [],
  },
  OrderLine: {
    warrantyforproductid: null,
    saleprice: null,
    ispurchaseordered: false,
  },
  Product: {
    customproperties: [],
    markerlinks: [],
    featurelinks: [],
    warrantylinks: [],
    relateditemlinks: [],
    sitedistributions: [],
    notes: [],
    packagelines: [],
  },
  ProductReorder: {
    productreorderdetails: [],
  },
  PurchaseOrder: {
    comments: [],
    purchaseorderdetails: [],
  },
  ReceivingAppointment: {
    status: 'O',
    appointmentdetails: [],
  },
  Report: {
    generators: [],
    outputs: [],
    type: 'R',
  },
  ServiceDetail: {
    ispurchaseordered: false,
  },
  Transfer: {
    comments: [],
    transferdetails: [],
  },
  TransferDetail: {
    ispurchaseordered: false,
  },
  Vendor: {
    comments: [],
    typelinks: [],
    markerlinks: [],
    productclassleads: [],
    disruptions: [],
    addresses: [],
  },
  VendorChargeback: {
    chargebackdetails: [],
    comments: [],
  },
};

const additionalPostV12Keys = {
  Product: {
    costs: [],
  },
};

/**
 * Creates a new empty model for specified class name.
 * Can disable value defaulting if explicitly told.
 * Can accept passedModelKeys - used in SearchAndTransfer.vue
 * @param {String} className - Class name to create model for.
 * @return {Object} - Empty model object for specified class name.
 * @todo: Consider adding additional flexible options for applicaiton state keys, uid, etc.
 */
const createModel = (className, passedModelKeys = null, shouldDefault = true) => {
  const properties = store.getters['meta/db_properties'](className);
  let model = { _classname: className, _id: ruid(), _isAdded: shouldDefault, _isDeleted: false };
  for (const { name, data } of properties) {
    const defaultValue = _get(data, 'property_meta.clientdefault');
    if (shouldDefault && isPresent(defaultValue)) model[name] = defaultValue;
    else model[name] = null;
  }
  if (shouldDefault) {
    const additionalKeys = _get(additionalModelKeys, className);
    if (additionalKeys) model = { ...model, ..._cloneDeep(additionalKeys) };
    if (store.getters['meta/gers_v12']) {
      const additionalV12Keys = _get(additionalPostV12Keys, className);
      if (additionalV12Keys) model = { ...model, ..._cloneDeep(additionalV12Keys) };
    }
  }
  if (passedModelKeys) model = { ...model, ...passedModelKeys };
  return model;
};

const drawImage = (coordinateArray) => {
  const data = coordinateArray;
  let canvas = document.createElement('canvas'); // We do the drawing/rendering of the signature on a canvas.
  const ctx = canvas.getContext('2d');
  let i = 0;
  let j = 0;
  let lowX = 255;
  let lowY = 127;
  let highX = 0;
  let highY = 0;

  canvas.width = 256;
  canvas.height = 128;
  canvas.alpha = 0;
  ctx.lineWidth = 2; // set line style.

  // draw the signature on canvas
  for (i = 0; i < data.length; i += 2) {
    j = i + 1;
    if (i === 0) ctx.moveTo(data[i], data[j]); // move to the first point.
    else if (data[j] === 255) { // Pen pickup, move to start new section/word.
      i += 2;
      j += 2;
      ctx.moveTo(data[i], data[j]);
    } else ctx.lineTo(data[i], data[j]);

    if (data[j] !== 255) { // keep track of boundaries so we can crop it afterwards
      if (data[i] < lowX) lowX = data[i];
      if (data[j] < lowY) lowY = data[j];
      if (data[i] > highX) highX = data[i];
      if (data[j] > highY) highY = data[j];
    }
  }

  ctx.stroke(); // paint the lines

  if (lowY > 0) lowY -= 1;
  else lowY = 0;

  // crop the image by creating a second canvas and using drawImage to draw only the relevant window of the original image (i.e. crop it)
  let destCanvas = document.createElement('canvas');
  destCanvas.width = highX - lowX;
  destCanvas.height = highY - lowY;

  const destCtx = destCanvas.getContext('2d');
  // image, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight, see https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage
  destCtx.drawImage(canvas, lowX, lowY, destCanvas.width, destCanvas.height, 0, 0, destCanvas.width, destCanvas.height);
  const imageSrc = destCanvas.toDataURL('image/png');
  // return just the data portion of the data URL so we need to strip out the starting bit that looks like this: data:image/png;base64,
  canvas = null;
  destCanvas = null;
  return imageSrc.slice(imageSrc.indexOf(';base64,') + ';base64,'.length);
};

const imageFromMagTekSignature = (dialogData) => {
  if (!_get(dialogData, '_result.signature.length')) return;

  const coordinateArray = dialogData._result.signature;
  dialogData.encodedData = drawImage(coordinateArray);
  return dialogData.encodedData;
};


const windowOpen = (url, target = undefined) => {
  url = `/${url}`;
  if (process.env.NODE_ENV === 'development') {
    // Don't let window add port to url.
    // This is probably fine in production as well, but limit to development:
    url = `https://${window.location.hostname}${url}`;
  }
  window.open(url, target || '_blank');
};


const openReportOutputs = (outputs, response = null) => {
  const reportError = _get(response, 'err_msg');
  const notifyDuration = _get(response, 'notification_fixed', null) ? { duration: -1 } : {};
  if (reportError) {
    store.dispatch('notify/push', {
      text: reportError,
      title: 'Error printing report.',
      ...notifyDuration,
    });
    return;
  }
  const notification = _get(response, 'success_notification');
  if (notification) {
    store.dispatch('notify/push', {
      title: notification,
      type: 'success',
      ...notifyDuration,
    });
  }
  outputs.forEach((report) => {
    windowOpen(report.url);
  });
};


const stripUIDsWhenPlainJSON = (obj) => {
  if (!obj || typeof obj !== 'object') return;
  if (!_isArray(obj) && !_has(obj, '_className') && !_has(obj, '_classname')) {
    for (const idKey of ['_id', 'uid']) _unset(obj, idKey);
  }
  for (const key of Object.keys(obj)) {
    if (typeof obj[key] === 'object') {
      if (!_isArray(obj[key])) stripUIDsWhenPlainJSON(obj[key]);
      else {
        for (const item of obj[key]) stripUIDsWhenPlainJSON(item);
      }
    }
  }
};

/**
 * Recursively mutates passed object in place to add uid properties to objects with a _classname property but without an existing uid property.
 * REFACTOR: Consider ending recursion on branches without a _classname property.
 */
const addUIDs = (obj) => {
  if (Array.isArray(obj)) return obj.map(item => addUIDs(item)); // Handle arrays by recursing on value
  if (typeof obj !== 'object' || obj === null) return obj; // Skip non-objects

  // Handle missing uid cases for objects with _classname, then recurse over children
  if (obj._classname && !obj.uid) obj.uid = ruid();
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [key, addUIDs(value)]),
  );
};


/**
 * @param {Object} product - New product to be set.
 * @param {Object} order - CustomerOrder.
 * @param {Object} detail - Orderline that product will be set on.
 * @param {Object} [setLineDetails = false] - Flag that determines if keys on orderline should be set.
 */
const lineUpdatesForNewProduct = ({ product, order, detail, setLineDetails = false, qtyordered = 1 }) => {
  if (product.prompts && product.prompts.length) product._displayReminders = true;
  const lineUpdates = { product, qtyordered };
  const prevProduct = detail.product;
  if (product.productid) {
    let resetSameProduct = false;

    if (_get(prevProduct, 'productid', '') === product.productid) {
      resetSameProduct = true;
    } else {
      lineUpdates.productid = product.productid;
    }

    if (setLineDetails) {
      // set price & other details (unless package component && same product)
      if (!resetSameProduct || !detail.packageid || !prevProduct || prevProduct.productid !== product.productid) {
        if (prevProduct) {
          const productClone = _cloneDeep(prevProduct);
          pruneForServerCall(productClone);
          // pass along the previous product and line details to the
          // server in case it wants to reject the change.
          lineUpdates.productchangeundo = {
            // These are strings and numbers (aside from product)
            // (dates would require special handling on server)
            product: productClone,
            productid: productClone.productid,
            saleprice: detail.saleprice,
            originalprice: detail.originalprice,
            originalalacarteprice: detail.originalalacarteprice,
            priceauthorizedby: detail.priceauthorizedby,
            priceoverridereason: detail.priceoverridereason,
            originalregularprice: detail.originalregularprice,
            promotionid: detail.promotionid,
            alacartepromotionid: detail.alacartepromotionid,
            setup: detail.setup,
            leaveincarton: detail.leaveincarton,
          };
        }

        addUIDs(lineUpdates.productchangeundo);

        const zeroType = order.type === 'SER' || order.type === 'EEX';

        // saleprice in NaN check needs to come from orderline
        if (!zeroType || isPresent(detail.saleprice)) {
          const productSale = isPresent(product.sale) ? product.sale : 9999999;
          const price = zeroType ? 0.0 : productSale;

          // Note that server's OrderDetail.add_new_line()
          // should do the same things we do client side if server adds line.
          if (resetSameProduct && detail.saleprice !== price) {
            store.dispatch('notify/push', {
              title: 'Pricing',
              text: store.getters['translations/rawTranslate']('PriceHasBeenReset'),
              type: 'warning',
            });
          }

          // Allow discount to be recalculated
          lineUpdates.discount = 0;

          if (!zeroType && product.requirepricing === constants.DB_TRUE) {
            // also set originalprice so price resetting requires
            // reprice (avoids using meaningless price)
            lineUpdates.saleprice = 0.0;
            lineUpdates.originalprice = 0.0;
            lineUpdates.originalalacarteprice = 0.0;

            store.dispatch('notify/push', {
              title: 'Pricing',
              text: store.getters['translations/rawTranslate']('PricingRequired'),
              type: 'warning',
            });
          } else {
            lineUpdates.saleprice = price;
            lineUpdates.originalprice = price;
            lineUpdates.originalalacarteprice = price;
          }

          // This is just a flag to tell the server to reset the pricedate
          // using its time. Also, then we can avoid grouping by pricedate,
          // avoid undo
          lineUpdates.newpricedate = 'Y';
          lineUpdates.priceauthorizedby = null;
          lineUpdates.priceoverridereason = null;
          lineUpdates.originalregularprice = product.regularprice;
          // translate '' to null to keep lines grouped together
          lineUpdates.promotionid = product.promotionid ? product.promotionid : null;
          lineUpdates.alacartepromotionid = lineUpdates.promotionid;

          if (!resetSameProduct) {
            lineUpdates.setup = product.setupneeded;
            lineUpdates.leaveincarton = product.cartonization;
          }
        }
      }
    }
  }
  return lineUpdates;
};


const getOrderId = ({ neworderid, orderid }) => neworderid || orderid;


const getMainOrderId = ({ newmainorderid, mainorderid }) => newmainorderid || mainorderid;


const calcItemPrice = (detail) => precisionRound((detail.saleprice || 0) - (detail.discount || 0));


/**
* Creates uql used for product look up based on the product_search_fields system parameter.
* @param {String} searchTerm - String entered by user to be used in uql.
* @return {String} uql string used for product querying.
*/
const createProductLookupUql = (searchTerm) => {
  const { CONTAINSIGNORECASE, DELIM } = constants;
  const searchFields = store.getters['meta/systemParameter']('product_search_fields');
  const fields = searchFields ? searchFields.split(',') : [];
  let searchString = `productid-${CONTAINSIGNORECASE}${searchTerm}${DELIM}`;
  for (const field of fields) {
    searchString += `${field}-${CONTAINSIGNORECASE}${searchTerm}${DELIM}`;
  }
  return `or(${searchString})${DELIM}`;
};


const digit = (char) => char >= '0' && char <= '9';


const parseDate = (value, isDeferredStatus = false) => {
  if (!value) return null;
  const { isValid } = DateTime.fromISO(value);
  if (isDeferredStatus && isValid) return value;
  const valueString = value.trim();
  if (valueString.length < 6) return null;
  let sections = 0;
  let digits = 0;
  let next = false;
  let delimiter = '';
  let dayString = '';
  let monthString = '';
  let yearString = '';
  for (const char of valueString.split('')) {
    if (digit(char)) {
      if (next) {
        if (delimiter) return null;
        next = false;
        sections += 1;
        digits = 1;
      }
      else {
        digits += 1;
      }
      if (sections > 1) {
        yearString += char;
      }
      else {
        if (sections) dayString += char;
        else monthString += char;

        if (digits === 2) next = true;
      }
    }
    else {
      // too many delimiters
      if (sections > 1) return null;
      if (delimiter && char !== delimiter) return null;
      delimiter = char;
      // should have already seen a delimiter, but didn't
      // OR user thinks they're entering a time?
      if (sections && delimiter === ':') return null;
      sections += 1;
      digits = 0;
      next = false;
    }
  }
  const monthNum = parseInt(monthString, 10); // number check
  const dayNum = parseInt(dayString, 10); // number check
  let yearNum = parseInt(yearString, 10); // number check
  // if year is 2 chars and is < 70 default to 20<year>
  if (yearString.length === 2 && yearNum < 70) yearNum += 2000;
  else if (yearString.length !== 4) return null;
  const dateParsed = DateTime.local(yearNum, monthNum, dayNum).toISODate();
  const { isValid: successful } = DateTime.fromISO(dateParsed);
  if (!successful) return null;
  return dateParsed;
};


const updateMeridiem = (dateTime, newMeridiem) => {
  if (!newMeridiem) return dateTime.format('A');
  const meridiem = newMeridiem.toUpperCase();
  const hour = dateTime.hour;
  const timeObj = { hour };
  if (meridiem === 'AM' && hour >= 12) timeObj.hour = hour - 12;
  else if (meridiem === 'PM' && hour < 12) timeObj.hour = hour + 12;
  return dateTime.set(timeObj);
};


const parseTime = (timeString) => {
  if (!timeString) return null;
  let inputFormat = '';
  let digitCount = 0;
  let meridiem = '';
  // Determine and set meridiem
  for (const char of timeString.split('')) {
    if ((/[apAP]/).test(char)) {
      meridiem = char;
      meridiem += 'M';
      break;
    }
  }
  // Strip out extraneous characters leaving only digits
  timeString = timeString.replace(/[^0-9]/g, '');
  // Count digits to determine time periods
  for (const char of timeString.split('')) {
    if (digit(char)) digitCount += 1;
  }
  // Set time-parsing format for time string
  if (digitCount === 0) return null; // Time contains no digits.
  if (digitCount > 6) return null; // Time contains too many digits.
  if (digitCount % 2 === 0) {
    inputFormat = 'HH';
  }
  else {
    inputFormat = 'H';
  }
  if (digitCount > 2) inputFormat += 'mm';
  if (digitCount > 4) inputFormat += 'ss';
  let time = DateTime.fromFormat(timeString, inputFormat);
  if (!time.isValid) {
    return null;
  }
  if (meridiem && time.hour <= 12) time = updateMeridiem(time, meridiem);
  // time variable must be a 24-hour formatted string
  // with colons for server to convert to seconds
  return time.toFormat('HH:mm:ss');
};


const trimSearchString = ({ value, meta, key }) => {
  if (!value || !meta || !key) return value;
  const shouldTrim = typeof value === 'string' &&
    _get(meta, `properties.${key}.type`) === 'string' &&
    !_get(meta, `properties.${key}.format`) &&
    !_get(meta, `properties.${key}.property_meta.flag`) &&
    !_get(meta, `properties.${key}.property_meta.allownewline`);
  return shouldTrim ? value.trim() : value;
};


const isEmptyProduct = (productid) => {
  return productid === EMPTY_PRODUCTID;
};


const getProductLabelText = ({ displaydescription, description, vendorid, vendorsku }) => {
  let text = '';
  let vendorText = vendorid;
  if (vendorsku) vendorText += ` (${vendorsku})`;
  if (displaydescription) text = `${displaydescription || description} - ${vendorText}`;
  else text = vendorText;
  return text;
};


const getReportMenu = async className => {
  const { EQUALS, DELIM } = constants;
  if (!className) return;
  const uql = `classname-${EQUALS}${className}${DELIM}`;
  const { results } = await store.dispatch('editObject/post', {
    url: '/tg/reportmenu/query',
    params: { q: uql, orderby: 'sequence', serialize: '_ui_label' },
  });
  return results;
};


/**
 * Fetch resolved ClassLink entries for an edit-screen record.
 * Classname comes from model._classname. Passes primary keys only so
 * the server resolves against persisted data (not dirty client values).
 * @param {Object} model - Current edit model
 * @returns {Promise<Array<{title: String, url: String, visibleonlyif: String}>>}
 */
const getClassLinks = async (model) => {
  const className = model?._classname;
  if (!className || !model || !model.original) return [];
  const primaryKeys = store.getters['meta/primary_keys'](className) || [];
  if (!primaryKeys.length) return [];
  // class_name is the class to load; not classname (ClassLink PK).
  const params = { class_name: className };
  for (const key of primaryKeys) {
    if (!isPresent(model[key])) return [];
    params[key] = model[key];
  }
  const { results } = await store.dispatch('editObject/post', {
    url: '/tg/classlink/links_for',
    params,
  });
  return results || [];
};


const printReport = async ({ reportid, args, model, additionalParams = null }) => {
  const params = { reportid, type: 'report' };
  for (const arg of args) {
    if (!arg.sourceattribute) continue;
    // get value from model unless it is a constant like CONSTANT:Y
    params[arg.name] = arg.sourceattribute.startsWith('CONSTANT:') ?
      arg.sourceattribute.substr(9) :
      model[arg.sourceattribute];
  }
  if (store.getters['auth/netReadersEnabled'] || store.getters['auth/dialogDevice']) {
    params.dialogdevicepresent = true;
  }
  const { results, response } = await store.dispatch('editObject/post', {
    url: '/tg/report/runreport',
    params: { ...params, ...additionalParams },
    returnResponse: true,
  });
  openReportOutputs(results, response);
};


const getIndexByUID = (arr, obj) => {
  return arr.findIndex((recordItem) => {
    if (!_has(recordItem, 'uid')) return false;
    return recordItem.uid === obj.uid;
  });
};


const getTransferID = ({ orderdate, siteid, sequence }) => {
  // YYYY-MM-DD
  const splitDate = orderdate.split('-');
  const dateID = `${splitDate[1]}${splitDate[2]}${splitDate[0].substring(3)}`;
  return `${dateID}${siteid}${sequence}`;
};


const getLineNumbers = (details) => {
  if (!details || details.length === 0) return { text: '' };
  const validDetails = details.filter(i => isPresent(i.lineid));
  const sortedDetails = validDetails.sort((a, b) => {
    // 1. Sort voided last
    if (a.voided === 'Y' && b.voided !== 'Y') return 1;
    if (a.voided !== 'Y' && b.voided === 'Y') return -1;
    // 2. Sort by lineid
    return String(a.lineid).localeCompare(
      String(b.lineid),
      undefined,
      { numeric: true },
    );
  });
  const lineNumbers = [];
  for (const i of sortedDetails) {
    let lineStr = String(i.lineid);
    if (i.voided === 'Y') {
      // grey, italic, strike-through
      lineStr = `<i class="text-grey text-decoration-line-through">${lineStr}</i>`;
    }
    lineNumbers.push(lineStr);
  }
  const allLinesStr = lineNumbers.join(', ');
  const TRUNC = 3;
  if (lineNumbers.length > TRUNC) {
    return { text: `${lineNumbers.slice(0, TRUNC).join(', ')}...`,
      message: allLinesStr };
  }
  return { text: allLinesStr };
};


const valueProductForLine = (orderLine, selectedOrder) => {
  const coveredProductId = orderLine.valueforproductid || orderLine.warrantyforproductid;
  if (!coveredProductId) {
    orderLine._valueProductId = 0;
    return orderLine;
  }
  const orderLines = _get(selectedOrder, 'orderlines', []);
  let masterLine = null;
  for (const line of orderLines) {
    if (line.productid === coveredProductId && line.qtyordered > 0) {
      masterLine = line;
      break;
    }
  }
  if (masterLine) {
    const { _minLineNumber, _minServerSequence, sequenceadded, uid } = masterLine;
    return {
      _minLineNumber,
      _minServerSequence,
      _valueProductId: 1,
      sequenceadded,
      uid,
    };
  }
  orderLine._valueProductId = 0;
  return orderLine;
};


const setMinLineAndSequence = (orderLine, currentSequence) => {
  if (!isPresent(orderLine.sequenceadded)) {
    orderLine.sequenceadded = currentSequence;
    currentSequence += 1;
  }
  if (!orderLine.orderdetails || !orderLine.orderdetails.length) {
    orderLine._minLineNumber = Infinity;
    orderLine._minServerSequence = Infinity;
    return currentSequence;
  }
  const lineNumbers = [];
  const serverSequences = [];
  for (const { lineid, serversequence } of orderLine.orderdetails) {
    if (isPresent(lineid)) lineNumbers.push(lineid);
    if (isPresent(serversequence)) serverSequences.push(serversequence);
  }
  orderLine._minLineNumber = lineNumbers.length ? Math.min(...lineNumbers) : Infinity;
  orderLine._minServerSequence = serverSequences.length ? Math.min(...serverSequences) : Infinity;
  return currentSequence;
};


const sortOrderLines = ({ order, orderlines, shouldSetSequence = true }) => {
  if (shouldSetSequence) {
    let currentSequence = store.getters['order/currentSequence'];
    for (const line of orderlines) {
      currentSequence = setMinLineAndSequence(line, currentSequence);
    }
    // Update the sequence if should change and did change
    if (shouldSetSequence && currentSequence > store.getters['order/currentSequence']) {
      store.dispatch('order/setSequence', currentSequence);
    }
  }
  // Ported from flex (OrderEdit.mxml)
  const sortedLines = orderlines.sort((a, b) => {
    if (a === b) return 0;
    const lineA = valueProductForLine(a, order);
    const lineB = valueProductForLine(b, order);
    if (lineA._minLineNumber < lineB._minLineNumber) return -1;
    if (lineA._minLineNumber > lineB._minLineNumber) return 1;
    if (lineA._minServerSequence < lineB._minServerSequence) return -1;
    if (lineA._minServerSequence > lineB._minServerSequence) return 1;
    if (lineA.sequenceadded < lineB.sequenceadded) return -1;
    if (lineA.sequenceadded > lineB.sequenceadded) return 1;
    if (lineA._valueProductId < lineB._valueProductId) return -1;
    if (lineA._valueProductId > lineB._valueProductId) return 1;
    // Guarantee that there are UIDs on the lines for the tiebreaker
    if (!isPresent(a.uid)) a.uid = ruid();
    if (!isPresent(b.uid)) b.uid = ruid();
    if (a.uid < b.uid) return -1;
    return 1;
  });
  return { sortedLines };
};


const sortObjectKeys = (object) => {
  const sortedObject = {};
  const sortedKeys = Object.keys(object).sort();
  for (const key of sortedKeys) {
    sortedObject[key] = object[key];
  }
  return sortedObject;
};

const skipObject = obj => {
  return obj && !Array.isArray(obj) && !obj._classname;
};

/**
 * @param {String} - Current key on transferObject to check.
 * @param {String} - Message type - error or warning.
 * @param {String[]} - Array of keys to exclude from displayed messages.
 * @return {Boolean} Determines if messages for message key shouldn't be added to displayed messages.
 */
const getFieldMessages = ({ model: mdl, msgKey, field, deep, displayFields, scope, excludedErrWarnFields }) => {
  // Helper functions (don't even bother setting them if we don't need them)
  const presentInTab = scope === 'tab' ?
    k => {
      for (const f of displayFields) {
        if (k === `_${f}${msgKey}`) return true;
      }
      return false;
    } : null;
  const excludeField = excludedErrWarnFields ?
    (key, mKey, excludedFields) => !!excludedFields.find(f => `_${f}${mKey}` === key) :
    null;
  const collectMessage = k => {
    return k && k !== msgKey && k.endsWith(msgKey) &&
      (!presentInTab || presentInTab(k)) &&
      (!excludeField || !excludeField(k, msgKey, excludedErrWarnFields));
  };

  // Message collection loop
  const messages = [];
  if (field) return mdl[`_${field}${msgKey}`] || [];
  if (deep) {
    _eachDeep(mdl, (value, key, parentValue, ctx) => {
      if (skipObject(parentValue)) return false;
      if (collectMessage(key)) {
        const item = _get(mdl, ctx.path);
        if (item) messages.push(...item);
      }
    });
  } else {
    for (const key of Object.keys(mdl)) {
      if (collectMessage(key)) messages.push(...mdl[key]);
    }
  }
  return messages;
};


const getObjectMessages = (mdl, msgKey, deep) => {
  const messages = [];
  if (deep) {
    _eachDeep(mdl, (value, key, parentValue, ctx) => {
      if (skipObject(parentValue)) return false;
      if (key && key === msgKey) {
        const item = _get(mdl, ctx.path);
        if (item) messages.push(...item);
      }
    });
  } else if (mdl[msgKey]) return [mdl[msgKey]];
  return messages;
};


/**
 * Coalesces all errors/warnings on passed model based on specified scope level.
 * @param {Object|Array} models - Object or array of object containing all
 * data pertinent to record. Used to coalesce errors/warnings against.
 * @param {String} scope - Indcates the level/scope for which errors/warnings
 * should be returned.
 * @return {Object[]} - Object containing an array of all errors (errorMessages)
 * and an array of all warnings (warningMessages).
 */
const getMessages = ({ models, scope, field, deep, deepObject, displayFields, includedRelationships = null, excludedErrWarnFields = null }) => {
  const messages = { errorMessages: [], warningMessages: [], hasMessages: false };
  if (!models || !scope) return messages;
  if (!Array.isArray(models) && typeof models === 'object') models = [models];
  for (const model of models) {
    if (_get(model, 'model') && _get(model, 'fields', []).length) {
      for (const fld of model.fields) {
        const msgs = getMessages({ models: model.model, scope: 'field', field: fld });
        messages.errorMessages = [...messages.errorMessages, ...msgs.errorMessages];
        messages.warningMessages = [...messages.warningMessages, ...msgs.warningMessages];
      }
    }
    if (scope === 'field' || scope === 'association' || scope === 'tab') {
      const payload = { model, field, deep, displayFields, scope, excludedErrWarnFields };
      messages.errorMessages.push(...getFieldMessages({ ...payload, msgKey: '_errors' }));
      messages.warningMessages.push(...getFieldMessages({ ...payload, msgKey: '_warnings' }));
    }
    if (scope === 'object' || scope === 'association') {
      messages.errorMessages.push(...getObjectMessages(model, '_errors', deepObject));
      messages.warningMessages.push(...getObjectMessages(model, '_warnings', deepObject));
    }
    if (includedRelationships) {
      for (const relationshipName of includedRelationships) {
        if (model[`_${relationshipName}_errors`]) {
          messages.errorMessages.push(model[`_${relationshipName}_errors`]);
        }
        if (model[`_${relationshipName}_warnings`]) {
          messages.warningMessages.push(model[`_${relationshipName}_warnings`]);
        }
      }
    }
  }

  if (messages.errorMessages.length || messages.warningMessages.length) messages.hasMessages = true;
  return messages;
};


const hasOriginal = (model) => !!_get(model, 'original', null);


/**
 * Inserts fields into collection via passed anchor element.
 * Commonly used for anchoring a field within meta data displayfields.
 * @param {Array} fields - Collection of values to insert into collection.
 * @param {String} anchor - Field to anchor fields against.
 * @param {Array} collection - Collection to add passed fields to based on anchor.
 * @return {Array} - reference to collection (newly modified, but same)
 */
const insertByAnchor = (fields, anchor, collection, key = 'name') => {
  const insertionPoint = _findIndex(collection, (item) => {
    if (typeof item === 'string') return item === anchor;
    return item[key] === anchor;
  });
  // Exit early if anchor point isn't found, appending to end instead
  if (insertionPoint === -1) collection.push(...fields);
  else collection.splice(insertionPoint + 1, 0, ...fields);
  return collection;
};


const getContainerSize = (width) => {
  if (width > 1904) return 'extra-large';
  if (width > 1264) return 'large';
  if (width > 960) return 'medium';
  if (width > 600) return 'small';
  if (width > 345) return 'extra-small';
  return 'extra-extra-small';
};


const getWatermark = (detail) => {
  if (detail.scanned) return 'SCANNED';
  if (detail._classname === 'OrderLine') {
    if (!detail.orderdetails) return '';
    for (const { istransferring, ispurchaseordered } of detail.orderdetails) {
      if (istransferring) return 'TRANSFER';
      if (ispurchaseordered) return 'ORDERED';
    }
  } else if ((detail.orderdetails && detail.orderdetails.length) ||
             (detail.servicedetails && detail.servicedetails.length)) return 'SOLD';
  if (detail.istransferring || detail.transferdetails && detail.transferdetails.length) return 'TRANSFER';
  if (detail.ispurchaseordered || detail.purchaseorderdetails && detail.purchaseorderdetails.length) return 'ORDERED';
  return '';
};


const hasLink = (detail) => {
  const { _classname, orderdetails, servicedetails, transferdetails, purchaseorderdetails, istransferring, ispurchaseordered } = detail;
  if (_classname === 'OrderLine') {
    if (!orderdetails) return false;
    return orderdetails.some(item => {
      return item.istransferring || item.ispurchaseordered;
    });
  }
  return istransferring || ispurchaseordered ||
         (orderdetails && orderdetails.length) ||
         (servicedetails && servicedetails.length) ||
         (transferdetails && transferdetails.length) ||
         (purchaseorderdetails && purchaseorderdetails.length);
};

/**
 * Creates and adds error messages to the model when
 * the passed in fields are either not present on the
 * model or don't have a value.
 *
 * @param {Object} model - Object to check fields against.
 * @param {String[]} fields - Array of keys to check on model for.
 * @param {String} [path = 'record'] - String used to update model in store.
 * @param {Function} messagesFunc - Returns array to be passed to translations/errors.
 * @param {Object} overrideMsg - Object containing messagesFuncs scoped to specific keys.
 * @return {Boolean} - Boolean value used where function is invoked
 *                     to either allow or prevent continued code execution.
 */
const createFieldMessages = ({ model, fields, path = 'record', messagesFunc, overrideMsg = null }) => {
  const missingFields = fields.filter((field) => !isPresent(model[field]));
  if (!missingFields.length) return !!missingFields.length;
  const keys = {};
  for (const field of missingFields) {
    let msgFunc = messagesFunc;
    if (_get(overrideMsg, field)) msgFunc = overrideMsg[field].messagesFunc;
    const messageText = store.getters['translations/error'](msgFunc(field));
    keys[`_${field}_errors`] = [messageText];
  }
  store.dispatch('editObject/updateMultipleValues', {
    keys,
    path,
  });
  return !!missingFields.length;
};


const addAnyMissingIds = (collection) => {
  for (let i = 0; i < collection.length; i += 1) {
    if (isPresent(collection[i]._id)) continue;
    collection[i]._id = ruid();
  }
};


const addEmptyOption = (payload) => {
  const { data = [], className, valueOverride = null, isEnum = false } = payload;

  if (isEnum) {
    const emptyOption = [{ label: '', value: null }];
    return emptyOption.concat(data);
  }

  if (className) {
    const dataValueField = store.getters['meta/data_value_field'](className);
    const emptyOption = [{ _isEmptyOption: true, _classname: className, _ui_label: '', [dataValueField]: valueOverride }];
    return emptyOption.concat(data);
  }
  return data;
};


/**
  * Adds a balance field to the transactions
  * @param {Object[]} transactions objects to have balance applied to
  * @param {bool} newestFirst preferende to determine which way the balances resolve
  * @param {string} balanceAttr attribute to add to the transaction
  * @param {bool} nullValues whether to null out balance values
  */
const calcResolvedBalance = (transactions, newestFirst, balanceAttr = 'resolvedbalance', nullValues = false) => {
  let current;
  let end;
  let step;
  if (newestFirst === constants.DB_TRUE) {
    current = transactions.length - 1;
    end = -1;
    step = -1;
  } else {
    current = 0;
    end = transactions.length;
    step = 1;
  }
  let bal = 0.0;
  while (current !== end) {
    const t = transactions[current];
    if (!nullValues) bal += Math.round(t.signedamount * 100) / 100.0;
    t[balanceAttr] = nullValues ? null : Number.parseFloat(bal).toFixed(2);
    current += step;
  }
};

const executeAction = async ({ data, selectedRecords, action, classname }) => {
  let url = '';
  if (action.implementation === 'R') url = '/tg/action/ruleaction';
  else url = `/tg/${action.classname.toLowerCase()}/${action.action}`;

  const params = { action_: action };

  for (const [key, value] of Object.entries(data)) {
    params[key] = JSON.stringify(value);
  }

  const { multiplerecords } = action;

  const className = `${classname.toLowerCase()}${multiplerecords === 'S' ? 's' : ''}`;
  const recordsToSend = multiplerecords === 'N' ? selectedRecords[0] : selectedRecords;
  params[className] = recordsToSend;

  let { results, refresh_others, errors, warnings, notification, notification_msg } = await post(url, params, true, { silentResponse: multiplerecords === 'C' });
  if (notification_msg && (errors || warnings) && multiplerecords === 'C') {
    notification = `${store.getters['translations/error'](notification)}`;
    notification_msg = `${store.getters['translations/error'](notification_msg)}`;
    const message = `${notification} - ${notification_msg}`;
    for (const record of results) {
      // TODO: Vue.set Remove once feeling comfortable with removing Vue.set
      // if (errors && !record._errors) Vue.set(record, '_errors', [[message]]);
      // if (warnings && !record._warnings) Vue.set(record, '_warnings', [[message]]);
      if (errors && !record._errors) _set(record, '_errors', [[message]]);
      if (warnings && !record._warnings) _set(record, '_warnings', [[message]]);
    }
  }

  return { results, refresh_others, errors, notification, notification_msg, warnings };
};

/**
 * Determines whether or not to include a line in Return Locations
 * @param {Object} line - detail to check for inclusion
 * @returns {Boolean}
 */
const includeLine = line => {
  const { product, productid, qtyordered, voided, scanned } = line;
  return productid !== EMPTY_PRODUCTID && _get(product, 'stockeditem') === 'Y' && qtyordered > 0 && voided !== 'Y' && !scanned;
};

const determineShowReturnLocations = (model, key) => {
  return _get(model, key, []).some(item => includeLine(item));
};

const hasFormData = ({ form, userInitiatedFields = [], dependentKeys = null, defaults = {} }) => {
  for (const key of Object.keys(defaults)) {
    if (!form[key]) _unset(defaults, key);
  }

  if (dependentKeys) {
    // remove key from form and defaults if it's a dependent value thit isn't in form
    for (const [key, value] of Object.entries(dependentKeys)) {
      if (!form[value]) _unset(form, key);
      if (defaults[key]) _unset(defaults, key);
    }
  }

  const keys = Object.keys(form);
  const payload = { formLength: keys.length };
  if (keys.length && !userInitiatedFields.length && _isEmpty(defaults)) return { ...payload, hasData: false };

  return { ...payload, hasData: Boolean(keys.length) };
};

/**
 * Return a formatted string for displaying a fillsiteid and filllocationid
 * @returns {string}
 */
const transferFillInfo = props => {
  const { orderdetails, fillsiteid, filllocationid } = props;
  let fillSiteId = fillsiteid;
  let fillLocationId = filllocationid;
  if (!fillSiteId && orderdetails) {
    for (const detail of orderdetails) {
      if (detail.fillsiteid && detail.filllocationid) {
        fillSiteId = detail.fillsiteid;
        fillLocationId = detail.filllocationid;
        break;
      }
    }
  }
  return fillSiteId && fillLocationId ? `${fillSiteId}: ${fillLocationId}` : '';
};

const getCookie = name => {
  const cookieName = `${name}=`;
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(';');

  for (let i = 0; i < cookieArray.length; i += 1) {
    let cookie = cookieArray[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(cookieName) === 0) {
      return cookie.substring(cookieName.length, cookie.length);
    }
  }
  return '';
};

const setCookie = (name, value, days) => {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${d.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/`;
};

const deleteCookie = name => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/`;
};

const updateCookie = (name, value, days) => {
  deleteCookie(name);
  setCookie(name, value, days);
};

const productSearchToolTipMessage = () => {
  const productSearchParameter = store.getters['meta/systemParameter']('product_search_fields');
  let productSearchFields = ['productid'];
  if (productSearchParameter) productSearchFields = productSearchFields.concat(productSearchParameter.split(','));

  // Construct tooltip for product filter
  const translatedFields = productSearchFields.map(field => store.getters['translations/rawTranslate'](`Product.${field}`));
  const length = translatedFields.length;
  let message = translatedFields[length - 1];
  if (length > 1) message = `${translatedFields[length - 2]} or ${message}`;
  if (length > 2) message = `${translatedFields.slice(0, length - 2).join(', ')}, ${message}`;
  return { message: [message], productSearchFields };
};

/**
 * Removes messages from object.
 * @param {Object|path} target - Object or path to object to remove messages from.
 * @param {String} key - Key to remove.
 */
const removeMessagesFromObj = (target, key) => {
  if (target?.[`_${key}_errors`]) _unset(target, `_${key}_errors`);
  if (target?.[`_${key}_warnings`]) _unset(target, `_${key}_warnings`);
};


const getRelativePath = (model, parentPath) => {
  // remove "record" and last index from path
  const detailPath = parentPath.replace(/(record\.|\[[0-9]+\]$)/g, '');
  const record = store.getters['editObject/record'];
  const details = _get(record, detailPath);
  const index = _findIndex(details, (item) => model._id === item._id);
  if (index !== -1) parentPath = `record.${detailPath}[${index}]`;

  return { index, parentPath };
};

const getSystemTheme = () => {
  const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  return isDark ? 'dark' : 'light';
};

export {
  reduce,
  hasErrors,
  getErrors,
  requestIdleCallback,
  mergeArraysByKey,
  roundTo,
  precisionRound,
  post,
  ruid,
  isPresent,
  operatorType,
  getButtonVariant,
  getOrderTypeColor,
  formatUqlDateText,
  formatUql,
  formatDateText,
  formatAtpFromLine,
  formatAtpDate,
  transformToInputItem,
  truncate,
  getValue,
  filterHeadersByPermission,
  formatImageURL,
  formatHeaders,
  getOrderedHeaders,
  formatValue,
  filterPrimaryKeys,
  translateMessages,
  generateMeta,
  getRequiredFields,
  calculateBalance,
  calculateTotal,
  generateLinkFromKey,
  compareDates,
  encodeImageData,
  moveElement,
  createModel,
  imageFromMagTekSignature,
  windowOpen,
  openReportOutputs,
  drawImage,
  stripUIDsWhenPlainJSON,
  lineUpdatesForNewProduct,
  getOrderId,
  getMainOrderId,
  calcItemPrice,
  createProductLookupUql,
  parseDate,
  parseTime,
  trimSearchString,
  updateMeridiem,
  isEmptyProduct,
  getProductLabelText,
  getReportMenu,
  printReport,
  getClassLinks,
  getIndexByUID,
  getTransferID,
  orderLineAtpDisplay,
  formatAtpBreakdown,
  qtyFilledLabel,
  formatFillDetails,
  getLineNumbers,
  sortOrderLines,
  valueProductForLine,
  setMinLineAndSequence,
  sortObjectKeys,
  getMessages,
  hasOriginal,
  insertByAnchor,
  getContainerSize,
  getWatermark,
  hasLink,
  createFieldMessages,
  addAnyMissingIds,
  addEmptyOption,
  calcResolvedBalance,
  executeAction,
  includeLine,
  determineShowReturnLocations,
  hasFormData,
  transferFillInfo,
  getCookie,
  setCookie,
  deleteCookie,
  updateCookie,
  productSearchToolTipMessage,
  removeMessagesFromObj,
  getRelativePath,
  getSystemTheme,
  isDayOp,
};
